"use client";

/**
 * `SwitchField` — boolean toggle rendered as a Radix Switch primitive.
 *
 * Same `Controller` rationale as `CheckboxField`: Radix Switch is a
 * controlled component (`checked` + `onCheckedChange`), not a native
 * `<input type="checkbox">`, so rhf's `register` doesn't bind directly.
 *
 * Layout differs from `CheckboxField` only by visual primitive — both
 * use the same flex-row label-on-right pattern. We could share a
 * `BooleanFieldShell` between them, but the two are nine lines each;
 * factoring it would obscure rather than clarify.
 *
 * Note on `onCheckedChange`: Radix Switch always emits a strict boolean
 * (no `"indeterminate"` like Checkbox), so we pass it straight through
 * to `rhf.onChange`.
 */

import * as React from "react";
import { Controller } from "react-hook-form";
import { Label, Switch } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { SwitchFieldDef } from "../types";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface SwitchFieldProps {
  field: SwitchFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

export function SwitchField({
  field,
  pathPrefix,
}: SwitchFieldProps): React.ReactElement | null {
  const { form } = useFormContext();

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const errorMessage = readErrorMessage(form.formState.errors, path);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Controller
          name={path}
          control={form.control}
          render={({ field: rhf }) => (
            <Switch
              id={path}
              checked={Boolean(rhf.value)}
              onCheckedChange={(next) => {
                rhf.onChange(next);
              }}
              onBlur={rhf.onBlur}
              ref={rhf.ref}
              aria-labelledby={`${path}-label`}
              aria-invalid={errorMessage !== undefined ? true : undefined}
              {...(field.disabled ? { disabled: true } : {})}
            />
          )}
        />
        <Label htmlFor={path} id={`${path}-label`}>
          {field.label}
        </Label>
      </div>
      {field.description ? (
        <p className="text-xs text-fg-muted">{field.description}</p>
      ) : null}
      {errorMessage ? (
        <p className="text-xs text-danger">{errorMessage}</p>
      ) : null}
    </div>
  );
}
